import { useState, useMemo } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { SideDrawer } from '@/components';
import { useNotificationRouter } from '@/common/hooks/useNotificationRouter';
import {
  getNotifications,
  markNotificationAsRead,
  markNotificationsAsReadBulk,
} from '../api';
import { Bell } from './Icons';
import { NotificationItem } from './NotificationItem';
import {
  groupUnreadNotifications,
  groupNotificationsByDate,
} from './notificationUtils';

export function NotificationDrawer({ isOpen, onClose, companyId }) {
  const queryClient = useQueryClient();
  const { handleNotificationNavigation } = useNotificationRouter();

  const [localReadIds, setLocalReadIds] = useState(() => new Set());

  // Infinite query for paginated notifications
  const {
    data: notificationsData,
    isLoading: isNotificationsLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: ['notifications', companyId],
    queryFn: ({ pageParam = 1 }) =>
      getNotifications(companyId, { page: pageParam }),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage?.next) {
        return allPages.length + 1;
      }
      return undefined;
    },
    enabled: !!companyId && isOpen,
  });

  // Flatten and deduplicate notifications by id
  const notificationsList = useMemo(() => {
    const rawList =
      notificationsData?.pages?.flatMap((page) => {
        if (Array.isArray(page?.results)) return page.results;
        if (Array.isArray(page)) return page;
        return [];
      }) || [];

    const seenIds = new Set();
    const deduplicated = [];
    for (const item of rawList) {
      if (item && item.id != null) {
        const idKey = String(item.id);
        if (!seenIds.has(idKey)) {
          seenIds.add(idKey);
          deduplicated.push(item);
        }
      }
    }
    return deduplicated;
  }, [notificationsData]);

  // Unread list in loaded data
  const unreadNotifications = useMemo(() => {
    return notificationsList.filter(
      (n) => !n.read_at && !localReadIds.has(n.id),
    );
  }, [notificationsList, localReadIds]);

  const loadedUnreadCount = unreadNotifications.length;

  // Single mark as read mutation
  const markReadMutation = useMutation({
    mutationFn: (notificationId) =>
      markNotificationAsRead({ companyId, notificationId }),
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({
        queryKey: ['notifications', companyId],
      });
      const previousNotifications = queryClient.getQueryData([
        'notifications',
        companyId,
      ]);

      queryClient.setQueryData(['notifications', companyId], (old) => {
        if (!old || !old.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            results: (page.results || []).map((n) =>
              String(n.id) === String(notificationId)
                ? { ...n, read_at: n.read_at || new Date().toISOString() }
                : n,
            ),
          })),
        };
      });

      return { previousNotifications };
    },
    onError: (err, variables, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          ['notifications', companyId],
          context.previousNotifications,
        );
      }
    },
  });

  // Bulk mark as read mutation (supporting grouped items and mark all)
  const markBulkMutation = useMutation({
    mutationFn: ({ notification_ids, mark_all }) =>
      markNotificationsAsReadBulk({
        companyId,
        notification_ids,
        mark_all,
      }),
    onMutate: async ({ notification_ids, mark_all }) => {
      await queryClient.cancelQueries({
        queryKey: ['notifications', companyId],
      });
      const previousNotifications = queryClient.getQueryData([
        'notifications',
        companyId,
      ]);

      const idSet = new Set((notification_ids || []).map(String));

      queryClient.setQueryData(['notifications', companyId], (old) => {
        if (!old || !old.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            results: (page.results || []).map((n) =>
              mark_all || idSet.has(String(n.id))
                ? { ...n, read_at: n.read_at || new Date().toISOString() }
                : n,
            ),
          })),
        };
      });

      return { previousNotifications };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['notifications', companyId] });
      if (variables?.mark_all) {
        toast.success('All notifications marked as read');
      }
    },
    onError: (err, variables, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          ['notifications', companyId],
          context.previousNotifications,
        );
      }
      toast.error('Failed to mark notifications as read');
    },
  });

  // Mark all as read handler
  const handleMarkAllAsRead = () => {
    if (markBulkMutation.isPending) return;
    const unreadIds = unreadNotifications.map((n) => n.id);
    if (unreadIds.length > 0) {
      setLocalReadIds((prev) => new Set([...prev, ...unreadIds]));
    }
    markBulkMutation.mutate({ notification_ids: [], mark_all: true });
  };

  // Click on a notification item: marks it as read and navigates
  const handleItemSelect = (notification) => {
    const unreadIds = (
      notification._notificationIds || [notification.id]
    ).filter((id) => !localReadIds.has(id));

    if (unreadIds.length > 0) {
      setLocalReadIds((prev) => {
        const next = new Set(prev);
        unreadIds.forEach((id) => next.add(id));
        return next;
      });

      if (unreadIds.length > 1) {
        markBulkMutation.mutate({
          notification_ids: unreadIds,
          mark_all: false,
        });
      } else {
        markReadMutation.mutate(unreadIds[0]);
      }
    }

    onClose?.();
    handleNotificationNavigation(notification);
  };

  // Group unread chat messages and bucket by date (Today, Yesterday, Earlier)
  const groupedSections = useMemo(() => {
    // 1. Group unread messages with same conversation_id & channel
    const withGroupedMessages = groupUnreadNotifications(
      notificationsList,
      localReadIds,
    );

    // 2. Group by Today, Yesterday, Earlier
    return groupNotificationsByDate(withGroupedMessages);
  }, [notificationsList, localReadIds]);

  const { today, yesterday, earlier } = groupedSections;
  const totalRendered = today.length + yesterday.length + earlier.length;

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      width="lg"
      className="notification-drawer"
      headerClassName="notification-drawer-header px-4 py-4 sm:px-6 sm:py-5"
      bodyClassName="notification-list p-4 sm:p-5 bg-white custom-scrollbar overscroll-y-contain"
      header={
        <div className="flex items-center justify-between w-full">
          <span className="text-xl font-bold font-sf-pro-display text-slate-900">
            Notifications
          </span>

          <div className="flex items-center gap-3">
            {loadedUnreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllAsRead}
                disabled={markBulkMutation.isPending}
                className="text-xs font-semibold text-primary hover:text-primary-hover disabled:opacity-50 transition-colors cursor-pointer rounded-md px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                {markBulkMutation.isPending ? 'Marking...' : 'Mark all as read'}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-[10px] bg-[#4BB543] text-white flex items-center justify-center hover:bg-[#3ea037] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#4BB543] shrink-0 cursor-pointer shadow-xs"
              aria-label="Close notifications"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      }
      onBodyScroll={(e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (
          scrollHeight > clientHeight &&
          scrollHeight - scrollTop <= clientHeight + 80
        ) {
          if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        }
      }}
    >
      <div className="flex flex-col gap-4">
        {/* Initial Loading Skeletons */}
        {isNotificationsLoading ? (
          <div className="flex flex-col gap-3">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="flex gap-3 p-4 rounded-2xl bg-white border border-[#EBEBEB] animate-pulse"
              >
                <div className="w-10 h-10 rounded-full bg-slate-200 shrink-0" />
                <div className="flex-1 flex flex-col gap-2 pt-1">
                  <div className="flex justify-between items-center gap-4">
                    <div className="h-4 bg-slate-200 rounded w-1/3" />
                    <div className="h-3 bg-slate-200 rounded w-12" />
                  </div>
                  <div className="h-3 bg-slate-200 rounded w-4/5" />
                  <div className="h-3 bg-slate-200 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : isError && notificationsList.length === 0 ? (
          /* Error state with retry when no notifications are loaded */
          <div className="py-12 flex flex-col items-center justify-center text-center gap-3 bg-white rounded-2xl border border-red-100 p-6">
            <div className="w-10 h-10 rounded-full bg-red-50 text-red-500 flex items-center justify-center">
              <Bell className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-text-primary">
                Failed to load notifications
              </p>
              <p className="text-xs text-text-muted mt-1 max-w-xs">
                {error?.message ||
                  'An error occurred while loading. Please try again.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-2 px-4 py-2 text-xs font-semibold rounded-lg bg-primary text-white hover:bg-primary-hover transition-colors"
            >
              Retry
            </button>
          </div>
        ) : totalRendered === 0 ? (
          /* Friendly Empty State */
          <div className="py-14 flex flex-col items-center justify-center text-center gap-3 bg-white rounded-2xl border border-[#EBEBEB] p-6">
            <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
              <Bell className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-bold text-text-primary">
                You’re all caught up
              </p>
              <p className="text-xs text-text-muted mt-1 max-w-xs">
                No notifications at this time. We will alert you when new
                updates arrive.
              </p>
            </div>
          </div>
        ) : (
          /* Grouped Notification Lists */
          <div className="flex flex-col gap-5">
            {/* Today */}
            {today.length > 0 && (
              <div className="flex flex-col gap-2.5">
                <div className="px-1 text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Today
                </div>
                <div className="flex flex-col gap-2.5">
                  {today.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      section="today"
                      isUnread={
                        !notification.read_at &&
                        !localReadIds.has(notification.id)
                      }
                      onSelect={handleItemSelect}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Yesterday */}
            {yesterday.length > 0 && (
              <div className="flex flex-col gap-2.5">
                <div className="px-1 text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Yesterday
                </div>
                <div className="flex flex-col gap-2.5">
                  {yesterday.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      section="yesterday"
                      isUnread={
                        !notification.read_at &&
                        !localReadIds.has(notification.id)
                      }
                      onSelect={handleItemSelect}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Earlier */}
            {earlier.length > 0 && (
              <div className="flex flex-col gap-2.5">
                <div className="px-1 text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Earlier
                </div>
                <div className="flex flex-col gap-2.5">
                  {earlier.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      section="earlier"
                      isUnread={
                        !notification.read_at &&
                        !localReadIds.has(notification.id)
                      }
                      onSelect={handleItemSelect}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Load More Button / Indicator */}
            {hasNextPage && (
              <div className="pt-2 pb-1 flex flex-col items-center justify-center">
                <button
                  type="button"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="px-4 py-2 text-xs font-semibold text-text-primary bg-white hover:bg-slate-50 border border-[#EBEBEB] rounded-xl shadow-xs transition-colors disabled:opacity-50"
                >
                  {isFetchingNextPage ? 'Loading more...' : 'Load more'}
                </button>
              </div>
            )}

            {/* Inline Error when fetching next page fails, preserving loaded items */}
            {isError && notificationsList.length > 0 && (
              <div className="p-3 bg-red-50 border border-red-100 rounded-xl flex items-center justify-between gap-3 text-xs text-red-700">
                <span>Failed to load more notifications.</span>
                <button
                  type="button"
                  onClick={() => fetchNextPage()}
                  className="font-bold underline hover:text-red-900"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </SideDrawer>
  );
}
